import { useEffect } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { DrawerLayout } from '@/components/layouts/drawer-layout';
import { Drawer } from '@/components/ui/drawer';
import { FlightServiceSummary } from '@/components/views/flight-service/summary';
import { Itinerary } from '@/components/views/itinerary';
import { OceanServiceSummary } from '@/components/views/ocean-service/summary';
import { RouteDetail } from '@/components/views/route/detail';
import { SearchRoutes } from '@/components/views/search/routes';
import { useDrawer } from '@/hooks/use-drawer';
import { MODE_AIR, MODE_WATER } from '@/lib/constants';
import { getOptionTransferLocationIds } from '@/lib/models/option/utils/get-option-transfer-location-ids';
import { selectedItineraryState } from '@/state/itinerary';
import { selectedOptionState } from '@/state/option';
import { selectedRouteState } from '@/state/routes';

export interface SearchLayoutProps {
  onSearch: () => void;
  onForceSearch?: () => void;
  onCreateSavedItem: () => void;
  onDeleteSavedItem: () => void;
  isRouteSaved?: boolean;
}

// This component controls all the drawers/blades that could be open on the
// route search view, as well as rendering the base route search view
const SearchLayout = ({
  onSearch,
  onForceSearch,
  onCreateSavedItem,
  onDeleteSavedItem,
  isRouteSaved = false,
}: SearchLayoutProps) => {
  const { isOpen: isRouteDetailOpen, closeDrawer: closeRouteDetail, openDrawer: openRouteDetail } = useDrawer();
  const { isOpen: isOptionDetailOpen, closeDrawer: closeOptionDetail, openDrawer: openOptionDetail } = useDrawer();
  const { isOpen: isItineraryOpen, closeDrawer: closeItinerary, openDrawer: openItinerary } = useDrawer();
  const selectedRoute = useRecoilValue(selectedRouteState);
  const selectedOption = useRecoilValue(selectedOptionState);
  const selectedItinerary = useRecoilValue(selectedItineraryState);
  const resetSelectedRoute = useResetRecoilState(selectedRouteState);
  const resetSelectedOption = useResetRecoilState(selectedOptionState);
  const resetSelectedItinerary = useResetRecoilState(selectedItineraryState);

  const onRouteDetailDrawerClose = () => {
    closeRouteDetail();
    resetSelectedRoute();
  };

  const onOptionDetailDrawerClose = () => {
    closeOptionDetail();
    resetSelectedOption();
  };

  const onItineraryDrawerClose = () => {
    closeItinerary();
    resetSelectedItinerary();
  };

  useEffect(() => {
    if (selectedRoute) {
      openRouteDetail();
    }
  }, [selectedRoute]);

  useEffect(() => {
    if (selectedOption) {
      openOptionDetail();
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedItinerary) {
      openItinerary();
    }
  }, [selectedItinerary]);

  return (
    <>
      <SearchRoutes
        onSearch={onSearch}
        onForceSearch={onForceSearch}
        onClickSaveItem={onCreateSavedItem}
        isRouteSaved={isRouteSaved}
        onDeleteSaveItem={onDeleteSavedItem}
      />
      <Drawer isOpen={isRouteDetailOpen} closeDrawer={onRouteDetailDrawerClose}>
        {isRouteDetailOpen && selectedRoute && (
          <DrawerLayout
            breadcrumbItems={[
              {
                label: 'Back to results',
                onClick: onRouteDetailDrawerClose,
              },
            ]}
          >
            <RouteDetail route={selectedRoute} />
          </DrawerLayout>
        )}
      </Drawer>
      <Drawer isOpen={isOptionDetailOpen} closeDrawer={onOptionDetailDrawerClose}>
        {isOptionDetailOpen && selectedRoute && selectedOption && (
          <DrawerLayout
            breadcrumbItems={[
              {
                label: 'Back to route overview',
                onClick: onOptionDetailDrawerClose,
              },
            ]}
          >
            {selectedRoute.primaryMode === MODE_AIR && (
              <FlightServiceSummary
                option={selectedOption}
                originId={selectedRoute?.fromId}
                destinationId={selectedRoute?.toId}
                carrierIds={selectedOption?.carrierIds}
                transferLocationIds={getOptionTransferLocationIds(selectedOption)}
              />
            )}
            {selectedRoute.primaryMode === MODE_WATER && (
              <OceanServiceSummary
                option={selectedOption}
                originId={selectedRoute?.fromId}
                destinationId={selectedRoute?.toId}
              />
            )}
            {selectedRoute.primaryMode !== MODE_WATER && selectedRoute.primaryMode !== MODE_AIR && (
              <div>Not implemented yet</div>
            )}
          </DrawerLayout>
        )}
      </Drawer>
      <Drawer isOpen={isItineraryOpen} closeDrawer={onItineraryDrawerClose}>
        {isItineraryOpen && selectedItinerary && (
          <DrawerLayout
            breadcrumbItems={[
              {
                label: 'Back to Options',
                onClick: onItineraryDrawerClose,
              },
            ]}
          >
            <Itinerary itinerary={selectedItinerary} />
          </DrawerLayout>
        )}
      </Drawer>
    </>
  );
};

export { SearchLayout };
